import { Settings } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'

import type { SoftwareAssuranceWorkflowController } from '@/hooks/workflow/useSoftwareAssuranceWorkflow'

interface ToolingStepSectionProps {
  workflow: SoftwareAssuranceWorkflowController
}

export function ToolingStepSection({ workflow }: ToolingStepSectionProps) {
  const navigate = useNavigate()

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Tool Selection</h4>
        <Button variant="outline" onClick={() => navigate('/metadataiq/processes')}>
          Manage Registry
          <Settings className="ml-2 h-4 w-4" />
        </Button>
      </div>
      <div className="grid gap-3">
        {workflow.tools.map((tool) => (
          <div key={tool.tool_id} className="rounded-lg border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={!!workflow.selectedTools[tool.tool_id]}
                  onCheckedChange={() => workflow.toggleTool(tool.tool_id)}
                />
                <div>
                  <p className="text-sm font-medium">{tool.name}</p>
                  <p className="text-xs text-muted-foreground">{tool.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={tool.endpoint_url ? 'default' : 'secondary'}>
                  {tool.endpoint_url ? 'Configured' : 'Needs endpoint'}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    workflow.setActiveToolConfigId((prev) =>
                      prev === tool.tool_id ? null : tool.tool_id
                    )
                  }
                >
                  Configure
                </Button>
              </div>
            </div>
            {workflow.activeToolConfigId === tool.tool_id && (
              <div className="grid gap-3 rounded-lg border border-border p-3 bg-muted/20">
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Endpoint URL</p>
                  <Input
                    placeholder="https://scanner.example.mil/run"
                    value={workflow.toolConfigDrafts[tool.tool_id]?.endpoint_url ?? ''}
                    onChange={(event) =>
                      workflow.setToolConfigDrafts((prev) => ({
                        ...prev,
                        [tool.tool_id]: {
                          endpoint_url: event.target.value,
                          bearer_token: prev[tool.tool_id]?.bearer_token ?? '',
                        },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Bearer Token (optional)</p>
                  <Input
                    type="password"
                    placeholder="Stored securely server-side"
                    value={workflow.toolConfigDrafts[tool.tool_id]?.bearer_token ?? ''}
                    onChange={(event) =>
                      workflow.setToolConfigDrafts((prev) => ({
                        ...prev,
                        [tool.tool_id]: {
                          endpoint_url: prev[tool.tool_id]?.endpoint_url ?? '',
                          bearer_token: event.target.value,
                        },
                      }))
                    }
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => workflow.handleSaveToolConfig(tool.tool_id)}>
                    Save Configuration
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Tokens are write-only and are not displayed after save.
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
